import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import {
  HorizontalPosition,
  NotificationBgColor,
  NotificationPosition,
  VerticalPosition,
} from 'store/models/Notification';

interface NotificationContextType {
  isNotificationOpened: boolean;
  textContent: string;
  notificationBgColor: NotificationBgColor;
  autoHideDuration: number;
  transitionDuration: number;
  anchorOriginConfig: NotificationPosition;
  setIsNotificationOpened: Dispatch<SetStateAction<boolean>>;
  setTextContent: Dispatch<SetStateAction<string>>;
  setNotificationBgColor: Dispatch<SetStateAction<NotificationBgColor>>;
  setTransitionDuration: Dispatch<SetStateAction<number>>;
  setAutoHideDuration: Dispatch<SetStateAction<number>>;
  setAnchorOriginConfig: Dispatch<SetStateAction<NotificationPosition>>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [isNotificationOpened, setIsNotificationOpened] = useState<boolean>(false);
  const [autoHideDuration, setAutoHideDuration] = useState<number>(5000);
  const [transitionDuration, setTransitionDuration] = useState<number>(500);
  const [textContent, setTextContent] = useState<string>('Success!');
  const [notificationBgColor, setNotificationBgColor] = useState(NotificationBgColor.SUCCESS);
  const [anchorOriginConfig, setAnchorOriginConfig] = useState<NotificationPosition>({
    vertical: VerticalPosition.TOP,
    horizontal: HorizontalPosition.RIGHT,
  });

  return (
    <NotificationContext.Provider
      value={{
        transitionDuration,
        isNotificationOpened,
        textContent,
        notificationBgColor,
        autoHideDuration,
        anchorOriginConfig,
        setIsNotificationOpened,
        setTextContent,
        setNotificationBgColor,
        setAutoHideDuration,
        setTransitionDuration,
        setAnchorOriginConfig,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
